import { useTranslation } from 'react-i18next'
import { LookPicture } from '../../ui/looks/LookPicture.tsx'
import { LookPicker } from '../../ui/looks/LookPicker.tsx'
import { pick, setMode, type ConfirmBlock, type DoneState } from '../../data/settingsDone.ts'
import type { LookFrame } from '../../data/lookGeometry.ts'

/** M25-F (docs/M25.md B, E; rule 10): the finish's « RÉGLAGES FAITS ». Controlled: pages/WorkFinish.tsx holds the state (data/settingsDone.ts: whether Terminer
 *  waits, the payload of job.confirm_settings@1) and sends it after job.finish@1. The plan as its pictures and the club's words, then two 56-px buttons —
 *  « Fait comme prévu » (one tap when nothing changed) and « Modifier » (each choice setting a `LookPicker` preset to the plan, « Non prévu » where the plan had
 *  none). A job that planned none of the settings gets « Indiquer les réglages faits » instead (optional: the pickers, all on « Non prévu »). The words are the
 *  club's; the pictures are aria-hidden (the words carry the meaning). Nothing here is written to the job. */
export function SettingsDone({ block, state, onChange, frame = 'round', disabled = false }: {
  block: ConfirmBlock; state: DoneState; onChange: (s: DoneState) => void; frame?: LookFrame; disabled?: boolean
}) {
  const { t } = useTranslation()
  const choosing = state.mode === 'modified' || state.mode === 'indicate'
  const btn = (on: boolean) => `btn text-lg ${on ? 'btn-primary' : ''}`
  return (
    <div className="flex flex-col gap-3" data-testid="settings-done" data-plan={block.hasPlan ? '1' : '0'} data-mode={state.mode ?? ''}>
      <h2 className="text-lg font-bold">{t('settingsDone.title')}</h2>
      {block.hasPlan ? (
        <>
          <p className="meta">{t('settingsDone.planHelp')}</p>
          <ul className="flex flex-col gap-1" data-testid="settings-done-plan">
            {block.plannedLines.map((l) => (
              <li key={l.key} className="flex items-center gap-2" data-testid="settings-done-plan-line" data-key={l.key}>
                {l.look ? <LookPicture code={l.look} frame={frame} size="schema" className="size-10 shrink-0 text-brand dark:text-fairway" /> : <span className="size-10 shrink-0" aria-hidden="true" />}
                <span><span className="meta">{l.label}</span>{' '}<span className="font-semibold">{l.value}</span></span>
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button type="button" className={btn(state.mode === 'planned')} aria-pressed={state.mode === 'planned'} disabled={disabled} onClick={() => onChange(setMode(state, 'planned'))} data-testid="settings-done-asplanned">{t('settingsDone.asPlanned')}</button>
            <button type="button" className={btn(state.mode === 'modified')} aria-pressed={state.mode === 'modified'} disabled={disabled} onClick={() => onChange(setMode(state, 'modified'))} data-testid="settings-done-modify">{t('settingsDone.modify')}</button>
          </div>
          {state.mode === 'modified' && <p className="meta">{t('settingsDone.modifyHelp')}</p>}
        </>
      ) : (
        <>
          <p className="meta">{t('settingsDone.optional')}</p>
          <button type="button" className={btn(state.mode === 'indicate')} aria-pressed={state.mode === 'indicate'} disabled={disabled}
            onClick={() => onChange(state.mode === 'indicate' ? { ...state, mode: null } : setMode(state, 'indicate'))} data-testid="settings-done-indicate">{t('settingsDone.indicate')}</button>
        </>
      )}
      {choosing && (
        <div className="flex flex-col gap-4" data-testid="settings-done-picks">
          {block.settings.map((s) => (
            <LookPicker key={s.key} legend={s.label} options={s.options} value={state.picks[s.key] ?? null} frame={frame} disabled={disabled} testId={`settings-done-pick-${s.key}`}
              none={s.planned === null ? t('settingsDone.notPlanned') : undefined} onChange={(v) => onChange(pick(state, s.key, v))} />
          ))}
        </div>
      )}
    </div>
  )
}
